// Miky Motorsport - zavodni HUD (M2): pozice/kolo/cas, rychlomer, minimapa, countdown, vysledky
import { RACE_STATE } from './race.js';

function el(cls) {
  const d = document.createElement('div');
  d.className = cls;
  return d;
}

function fmtTime(s) {
  if (s == null || !isFinite(s)) return '--:--';
  const m = Math.floor(s / 60);
  const sec = s - m * 60;
  return `${m}:${sec.toFixed(2).padStart(5, '0')}`;
}

function gearLabel(v) {
  const sp = v.speedMs;
  if (sp < -0.6) return 'R';
  if (Math.abs(sp) < 0.6 && v.controls.throttle < 0.05) return 'N';
  return String(v.gear + 1);
}

export class RaceHUD {
  constructor(cb = {}) {
    this.cb = cb;
    const hud = document.getElementById('hud');
    hud.innerHTML = '';

    this.info = el('race-info');
    this.elPos = el('ri-pos');
    this.elLap = el('ri-lap');
    this.elTime = el('ri-time');
    this.info.append(this.elPos, this.elLap, this.elTime);
    hud.appendChild(this.info);

    this.speed = el('speed-readout');
    this.speed.innerHTML = '<div class="kmh">0<span>km/h</span></div>';
    this.elKmh = this.speed.querySelector('.kmh');
    hud.appendChild(this.speed);

    // Analogovy sdruzeny budik (otackomer + prevod + rychlost) vpravo dole
    this.gauge = document.createElement('canvas');
    this.gauge.className = 'tacho';
    const GZ = 196, dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.gauge.width = GZ * dpr; this.gauge.height = GZ * dpr;
    this.gauge.style.width = GZ + 'px'; this.gauge.style.height = GZ + 'px';
    this.gaugeCtx = this.gauge.getContext('2d');
    this.gaugeCtx.scale(dpr, dpr);
    this._gz = GZ;
    hud.appendChild(this.gauge);

    this.miniWrap = el('minimap');
    this.mini = document.createElement('canvas');
    this.mini.width = 190; this.mini.height = 150;
    this.miniWrap.appendChild(this.mini);
    this.miniCtx = this.mini.getContext('2d');
    hud.appendChild(this.miniWrap);

    this.countdown = el('countdown hidden');
    hud.appendChild(this.countdown);

    this.modeInfo = el('mode-info hidden'); // eliminace/stihacka info
    hud.appendChild(this.modeInfo);

    this.results = el('results hidden');
    hud.appendChild(this.results);
    this._resultsSig = null;
    this._modeSig = null;

    this.pause = el('pause-overlay hidden');
    this.pause.innerHTML = '<div class="po-box"><div class="po-title">PAUZA</div><div class="po-hint">ESC pokračovat &middot; Enter restart &middot; M zpět do menu &middot; T reset auta</div></div>';
    hud.appendChild(this.pause);

    this._miniBounds = null;
  }

  update(race, vehicle, paused) {
    this.elKmh.innerHTML = `${Math.round(Math.abs(vehicle.speedKmh))}<span>km/h</span>`;
    this._drawGauge(vehicle);

    const p = race.player;
    this.elPos.innerHTML = `<b>${p.position}</b><small>/${race.cars.length}</small>`;
    const totalLaps = race.laps ?? race.track.laps;
    const lap = Math.max(1, Math.min(totalLaps, p.lap + (p.started ? 1 : 0)));
    this.elLap.textContent = `KOLO ${lap}/${totalLaps}`;
    const lapT = (race.state === RACE_STATE.RACING && p.started) ? race.raceTime - p.lapStartTime : 0;
    this.elTime.textContent = fmtTime(lapT);

    // countdown
    if (race.state === RACE_STATE.COUNTDOWN) {
      const c = Math.ceil(race.countdown - 1);
      this.countdown.textContent = c > 0 ? String(c) : 'START!';
      this.countdown.classList.remove('hidden');
    } else if (race.state === RACE_STATE.RACING && race.raceTime < 1.0) {
      this.countdown.textContent = 'START!';
      this.countdown.classList.remove('hidden');
    } else {
      this.countdown.classList.add('hidden');
    }

    // Info o rezimu (eliminace odpocet / stihaci start)
    this._renderModeInfo(race);

    // Leaderboard: ihned po dojezdu hrace, dynamicky se doplnuje pri prijezdu soupere
    // (i pri DNF hrace, kdyz zavod skonci stropem)
    if (race.player.finished || race.state === RACE_STATE.FINISHED) this._renderResults(race);

    this.pause.classList.toggle('hidden', !paused);
    this._drawMini(race);
  }

  _renderModeInfo(race) {
    const racing = race.state === RACE_STATE.RACING;
    let html = null, sig = null;
    if (race.mode === 'elimination' && racing) {
      const active = race.cars.filter((c) => !c.eliminated && !c.finished).length;
      const isLast = race.player.position >= active && !race.player.eliminated && !race.player.finished;
      const duel = active <= 2;
      sig = `e${active}|${isLast}|${duel}`;
      html = duel
        ? '<span class="mi-tag">FINÁLE</span> poslední kolo – <b>DUEL o vítězství!</b>'
        : `<span class="mi-tag">VYŘAZOVÁNÍ</span> každé kolo vypadne poslední · zbývá ${active}`
          + (isLast ? ' <b class="mi-danger">JSI POSLEDNÍ!</b>' : '');
    } else if (race.mode === 'pursuit' && racing && race.raceTime < race.pursuitHold) {
      const t = Math.ceil(race.pursuitHold - race.raceTime);
      sig = `p${t}`;
      html = `<span class="mi-tag">STÍHAČKA</span> start za <b>${t}s</b> – dožeň pole!`;
    } else if ((race.mode === 'duel' || race.mode === 'sprint') && racing && race.raceTime < 3) {
      sig = `m${race.mode}`;
      html = `<span class="mi-tag">${race.mode === 'duel' ? 'DUEL 1v1' : 'SPRINT'}</span>`;
    }
    if (sig === null) {
      if (this._modeSig !== null) { this.modeInfo.classList.add('hidden'); this._modeSig = null; }
      return;
    }
    if (sig !== this._modeSig) { // prepis DOM jen pri zmene (ne kazdy frame)
      this._modeSig = sig;
      this.modeInfo.innerHTML = html;
      this.modeInfo.classList.remove('hidden');
    }
  }

  _renderResults(race) {
    if (race.timeTrial) return this._renderTimeTrialResult(race);
    const finishedCount = race.cars.filter((c) => c.finished).length;
    const final = race.state === RACE_STATE.FINISHED;
    // rebuild jen kdyz se neco zmeni (novy dojezd / finalni stav / zmena pozice hrace)
    const sig = `${finishedCount}|${final}|${race.player.position}`;
    if (sig === this._resultsSig) return;
    this._resultsSig = sig;

    const rows = race.results().map((r) => {
      const c = '#' + r.color.toString(16).padStart(6, '0');
      let cell;
      if (r.eliminated) {
        cell = '<span class="onTrack">vyřazen</span>';
      } else if (r.finished) {
        cell = (r.gap != null && r.gap > 0.05) ? `+${r.gap.toFixed(1)}s` : '<span class="lead">vítěz</span>';
      } else {
        cell = '<span class="onTrack">na trati…</span>';
      }
      return `<div class="res-row${r.isPlayer ? ' me' : ''}${r.finished && !r.eliminated ? ' done' : ''}">
        <span class="res-pos">${r.position}</span>
        <span class="res-dot" style="background:${c}"></span>
        <span class="res-name">${r.name}${r.isRival ? ' ⚔️' : ''}</span>
        <span class="res-tot">${cell}</span></div>`;
    }).join('');

    const myPos = race.player.position;
    const pf = race.player.finished;
    const elim = race.mode === 'elimination';
    let title;
    if (!final) title = 'V CÍLI!';
    else if (elim) title = race.player.eliminated ? 'VYŘAZEN' : (myPos === 1 ? 'PŘEŽIL JSI! 🏆' : `SKONČIL JSI ${myPos}.`);
    else title = pf ? (myPos === 1 ? 'VYHRÁL JSI! 🏆' : `DOJEL JSI ${myPos}.`) : 'NEDOJEL JSI';
    const foot = final
      ? `<button class="btn btn-primary res-continue" data-continue>Pokračovat</button><div class="res-hint">nebo stiskni R</div>`
      : `<div class="res-hint">čeká se na dojezd soupeřů…</div>`;
    this.results.innerHTML = `<div class="res-box">
      <img class="res-miky${myPos === 1 && final ? ' win' : ''}" src="./img/miky-avatar.jpg" alt="Miky" draggable="false">
      <div class="res-title">${title}</div>
      <div class="res-head"><span class="res-pos">#</span><span class="res-dot"></span><span class="res-name">Jezdec</span><span class="res-tot">Odstup</span></div>
      ${rows}${foot}</div>`;
    const btn = this.results.querySelector('[data-continue]');
    if (btn) btn.onclick = () => { if (this.cb.onContinue) this.cb.onContinue(); };
    this.results.classList.toggle('live', !final); // lehci ztmaveni dokud bezi zavod
    this.results.classList.remove('hidden');
  }

  _renderTimeTrialResult(race) {
    const final = race.state === RACE_STATE.FINISHED;
    const sig = `tt|${final}`;
    if (sig === this._resultsSig) return;
    this._resultsSig = sig;
    if (!final) { // jeste jede - nic neukazuj
      this.results.classList.add('hidden');
      return;
    }
    const p = race.player;
    const total = p.finishTime, best = p.bestLap === Infinity ? null : p.bestLap;
    const pb = race.ttPB; // ms
    const totalMs = total * 1000;
    const isRecord = pb == null || totalMs < pb;
    let pbLine;
    if (pb == null) pbLine = '<div class="tt-rec">🏆 PRVNÍ ČAS!</div>';
    else if (isRecord) pbLine = `<div class="tt-rec">🏆 NOVÝ REKORD! &minus;${fmtTime((pb - totalMs) / 1000)}</div>`;
    else pbLine = `<div class="tt-pb">Osobní rekord: ${fmtTime(pb / 1000)} (+${fmtTime((totalMs - pb) / 1000)})</div>`;

    this.results.innerHTML = `<div class="res-box">
      <img class="res-miky${isRecord ? ' win' : ''}" src="./img/miky-avatar.jpg" alt="Miky" draggable="false">
      <div class="res-title">⏱ ${fmtTime(total)}</div>
      <div class="tt-rows">
        <div class="tt-row"><span>Nejlepší kolo</span><b>${best ? fmtTime(best) : '-'}</b></div>
        <div class="tt-row"><span>Celkový čas</span><b>${fmtTime(total)}</b></div>
      </div>
      ${pbLine}
      <button class="btn btn-primary res-continue" data-continue>Pokračovat</button>
      <div class="res-hint">nebo stiskni R</div>
    </div>`;
    const btn = this.results.querySelector('[data-continue]');
    if (btn) btn.onclick = () => { if (this.cb.onContinue) this.cb.onContinue(); };
    this.results.classList.remove('live');
    this.results.classList.remove('hidden');
  }

  _drawGauge(v) {
    const ctx = this.gaugeCtx;
    const Z = this._gz;
    ctx.clearRect(0, 0, Z, Z);
    const cx = Z / 2, cy = Z / 2, R = Z / 2 - 16;
    const redline = (v.spec && v.spec.engine && v.spec.engine.redlineRpm) || 6600;
    const shiftUp = (v.spec && v.spec.drivetrain && v.spec.drivetrain.shiftUpRpm) || redline * 0.86;
    const gMax = redline;
    const rpm = Math.max(0, Math.min(v.engineRpm, gMax));
    const A0 = Math.PI * 0.75, SWEEP = Math.PI * 1.5; // 135deg start, 270deg sweep
    const ang = (f) => A0 + Math.max(0, Math.min(1, f)) * SWEEP;
    const redF = shiftUp / gMax;
    const f = rpm / gMax;

    // pozadi
    ctx.fillStyle = 'rgba(8,10,16,0.72)';
    ctx.beginPath(); ctx.arc(cx, cy, R + 12, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = 'rgba(255,255,255,0.08)'; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.arc(cx, cy, R + 12, 0, Math.PI * 2); ctx.stroke();

    // track + redline zona
    ctx.lineCap = 'round'; ctx.lineWidth = 9;
    ctx.strokeStyle = '#262a36';
    ctx.beginPath(); ctx.arc(cx, cy, R, ang(0), ang(1)); ctx.stroke();
    ctx.strokeStyle = 'rgba(255,59,48,0.45)';
    ctx.beginPath(); ctx.arc(cx, cy, R, ang(redF), ang(1)); ctx.stroke();

    // hodnota (barva dle zony)
    ctx.strokeStyle = f >= redF ? '#ff3b30' : (f >= 0.72 ? '#ffb13a' : '#39d98a');
    ctx.lineWidth = 9;
    ctx.beginPath(); ctx.arc(cx, cy, R, ang(0), ang(f)); ctx.stroke();

    // ticky + cisla (po 1000)
    ctx.fillStyle = '#8a8fa0'; ctx.font = '600 12px system-ui, sans-serif';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    const ticks = Math.floor(gMax / 1000);
    for (let i = 0; i <= ticks; i++) {
      const tf = (i * 1000) / gMax;
      const a = ang(tf);
      const ca = Math.cos(a), sa = Math.sin(a);
      const isRed = (i * 1000) >= shiftUp;
      ctx.strokeStyle = isRed ? '#ff5b52' : '#5a6072'; ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(cx + ca * (R - 14), cy + sa * (R - 14));
      ctx.lineTo(cx + ca * (R - 6), cy + sa * (R - 6));
      ctx.stroke();
      ctx.fillStyle = isRed ? '#ff7b72' : '#8a8fa0';
      ctx.fillText(String(i), cx + ca * (R - 26), cy + sa * (R - 26));
    }

    // rucicka
    const na = ang(f);
    ctx.strokeStyle = '#ff5e3a'; ctx.lineWidth = 3; ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(cx - Math.cos(na) * 12, cy - Math.sin(na) * 12);
    ctx.lineTo(cx + Math.cos(na) * (R - 10), cy + Math.sin(na) * (R - 10));
    ctx.stroke();
    ctx.fillStyle = '#ff5e3a'; ctx.beginPath(); ctx.arc(cx, cy, 7, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = '#161826'; ctx.beginPath(); ctx.arc(cx, cy, 3, 0, Math.PI * 2); ctx.fill();

    // stred: prevod + male RPM
    ctx.fillStyle = '#fff'; ctx.font = '800 30px system-ui, sans-serif';
    ctx.fillText(gearLabel(v), cx, cy + 30);
    ctx.fillStyle = '#7a7f90'; ctx.font = '600 10px system-ui, sans-serif';
    ctx.fillText('PŘEVOD', cx, cy + 50);
    ctx.fillStyle = '#b8bcc8'; ctx.font = '700 12px system-ui, sans-serif';
    ctx.fillText(`${Math.round(rpm)} ot/min`, cx, cy - 34);
    ctx.fillStyle = '#5a6072'; ctx.font = '600 9px system-ui, sans-serif';
    ctx.fillText('×1000', cx + R - 30, cy + 8);
  }

  _drawMini(race) {
    const ctx = this.miniCtx;
    const W = this.mini.width, H = this.mini.height;
    ctx.clearRect(0, 0, W, H);
    const cl = race.track.centerline;
    if (!this._miniBounds) {
      let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
      for (const p of cl) { minX = Math.min(minX, p.x); maxX = Math.max(maxX, p.x); minZ = Math.min(minZ, p.z); maxZ = Math.max(maxZ, p.z); }
      const pad = 14;
      const sx = (W - pad * 2) / (maxX - minX);
      const sz = (H - pad * 2) / (maxZ - minZ);
      const s = Math.min(sx, sz);
      this._miniBounds = { minX, minZ, s, ox: W / 2 - ((minX + maxX) / 2) * s, oz: H / 2 + ((minZ + maxZ) / 2) * s };
    }
    const b = this._miniBounds;
    const px = (x) => b.ox + x * b.s;
    const py = (z) => b.oz - z * b.s;

    // trat
    ctx.strokeStyle = 'rgba(255,255,255,0.5)';
    ctx.lineWidth = 5;
    ctx.beginPath();
    for (let i = 0; i <= cl.length; i++) { const p = cl[i % cl.length]; const X = px(p.x), Y = py(p.z); i === 0 ? ctx.moveTo(X, Y) : ctx.lineTo(X, Y); }
    ctx.stroke();

    // auta
    for (const car of race.cars) {
      const X = px(car.curr.pos.x), Y = py(car.curr.pos.z);
      ctx.beginPath();
      ctx.arc(X, Y, car.isPlayer ? 5 : 3.5, 0, Math.PI * 2);
      ctx.fillStyle = car.isPlayer ? '#fff' : '#' + car.color.toString(16).padStart(6, '0');
      ctx.fill();
      if (car.isPlayer) { ctx.lineWidth = 2; ctx.strokeStyle = '#ff5e3a'; ctx.stroke(); }
    }
  }
}
